import { __DEV__ } from '@mote/helpers/constants'
import { mergeClassnames } from '@mote/helpers/dom'
import type { SystemProps } from '@xstyled/styled-components'
import styled, { css } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'
import { DialogFocus } from './DialogFocus'
import { useDialogContext } from './DialogProvider'
import { DialogTransition } from './DialogTransition'

function getMaxWidth(
  value: SystemProps['maxWidth'],
  useAbsolutePosition: boolean
): SystemProps {
  if (value === 'full') {
    const commonProps = {
      my: 0,
      mx: 0,
      borderRadius: 0
    }

    if (useAbsolutePosition) {
      return {
        ...commonProps,
        maxW: '100%',
        minH: '100%'
      }
    }

    return {
      ...commonProps,
      maxW: '100vw',
      minH: CSS.supports('min-height', '-webkit-fill-available')
        ? '-webkit-fill-available'
        : '100vh'
    }
  }

  return { maxW: value }
}

export interface DialogContainerProps extends HTMLXStyledProps<'section'> {
  containerProps?: HTMLXStyledProps<'div'>
}

export const DialogContainer = forwardRef<DialogContainerProps, 'section'>(
  (props, ref) => {
    const { className, children, containerProps: rootProps, ...rest } = props

    const {
      getDialogProps,
      getDialogContainerProps,
      motionPreset,
      isOpen,
      isCentered,
      scrollBehavior,
      maxWidth,
      useAbsolutePosition
    } = useDialogContext()

    const dialogProps = getDialogProps(rest, ref)
    const containerProps = getDialogContainerProps(rootProps)

    const _className = mergeClassnames('mote-dialog', className)
    const dialogStyles = {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      w: 'full',
      outline: 0,
      borderRadius: 'lg',
      bg: 'white',
      color: 'inherit',
      m: '60rpx 6',
      zIndex: 10000,
      boxShadow: 'lg',
      maxH: scrollBehavior === 'inside' ? 'calc(100% - 7.5rem)' : undefined
    } as SystemProps

    return (
      <DialogFocus>
        <DialogContainerRoot
          {...containerProps}
          alignItems={isCentered ? 'center' : 'flex-start'}
          overflow={scrollBehavior === 'inside' ? 'hidden' : 'auto'}
          className='mote-dialog__container'
          tabIndex={-1}
          useAbsolutePosition={useAbsolutePosition}
        >
          <DialogTransition
            preset={motionPreset}
            isOpen={isOpen}
            className={_className}
            {...dialogStyles}
            {...getMaxWidth(maxWidth, useAbsolutePosition)}
            {...dialogProps}
          >
            {children}
          </DialogTransition>
        </DialogContainerRoot>
      </DialogFocus>
    )
  }
)

if (__DEV__) {
  DialogContainer.displayName = 'DialogContainer'
}

const DialogContainerRoot = styled.divBox<{ useAbsolutePosition: boolean }>`
  display: flex;
  z-index: 10000;
  justify-content: center;
  left: 0;
  top: 0;

  ${props =>
    props.useAbsolutePosition
      ? css`
          position: absolute;
          width: 100%;
          height: 100%;
        `
      : css`
          position: fixed;
          width: 100vw;
          height: 100vh;

          @supports (height: -webkit-fill-available) {
            height: -webkit-fill-available;
          }
        `};

  > * {
    font-family: sans !important;
  }
`
